import { useState } from 'react';
import { Cpu, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2 } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import type { DeploymentStats } from '@/types/metrics';

interface ResourceAlertCardProps {
  resourceAlerts: DeploymentStats['resourceAlerts'];
}

function usageBarColor(value: number): string {
  if (value > 90) return 'dls-bg-status-offline';
  if (value >= 70) return 'dls-bg-status-warning';
  return 'dls-bg-status-online';
}

function UsageBar({ label, value }: { label: string; value: number | null }) {
  if (value === null) return null;
  const rounded = Math.round(value);
  return (
    <div className="dls-flex dls-items-center dls-gap-1.5">
      <span className="dls-w-8 dls-text-muted-foreground dls-text-[10px]">{label}</span>
      <div className="dls-flex-1 dls-h-1.5 dls-bg-muted dls-rounded-full dls-overflow-hidden">
        <div
          className={`dls-h-full dls-rounded-full ${usageBarColor(rounded)}`}
          style={{ width: `${rounded}%` }}
        />
      </div>
      <span
        className={`dls-w-8 dls-text-right dls-text-[10px] dls-font-medium ${rounded > 90 ? 'dls-text-status-offline' : rounded >= 70 ? 'dls-text-status-warning' : 'dls-text-muted-foreground'}`}
      >
        {rounded}%
      </span>
    </div>
  );
}

export function ResourceAlertCard({ resourceAlerts }: ResourceAlertCardProps) {
  const [expanded, setExpanded] = useState(true);
  const hasCritical = resourceAlerts.criticalCount > 0;
  const hasFlagged = resourceAlerts.totalFlagged > 0;
  const { totalAlive, totalReporting } = resourceAlerts;
  const coveragePct = totalAlive > 0 ? Math.round((totalReporting / totalAlive) * 100) : 0;

  // Card styling: red if critical, amber if caution-only, green/neutral if all clear
  const cardStyle = hasCritical
    ? 'dls-border-status-offline-tertiary dls-bg-status-offline-quarternary/50'
    : hasFlagged
      ? 'dls-border-status-warning-tertiary dls-bg-status-warning-quarternary/50'
      : 'dls-border-status-online-tertiary dls-bg-status-online-quarternary/50';

  const headerStyle = hasCritical
    ? 'dls-text-status-offline'
    : hasFlagged
      ? 'dls-text-status-warning'
      : 'dls-text-status-online';

  return (
    <Card className={`dls-flex dls-flex-col dls-h-full ${cardStyle}`}>
      <CardHeader className="dls-pb-2">
        <CardTitle className="dls-flex dls-items-center dls-justify-between dls-text-base">
          <div className={`dls-flex dls-items-center dls-gap-2 ${headerStyle}`}>
            <Cpu className="dls-h-4.5 dls-w-4.5" />
            <span>
              {hasFlagged ? (
                <>
                  {resourceAlerts.totalFlagged} Node{resourceAlerts.totalFlagged !== 1 ? 's' : ''} with
                  High Resource Usage
                </>
              ) : (
                'Resource Utilization'
              )}
            </span>
            <InfoTooltip text="Nodes with CPU, RAM, or filesystem usage above threshold. Caution (yellow) at 70%+, critical (red) at 90%+. Sustained high usage may indicate capacity issues or runaway processes." />
          </div>
          {hasFlagged && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="dls-text-muted-foreground hover:dls-text-foreground"
            >
              {expanded ? <ChevronUp className="dls-h-4 dls-w-4" /> : <ChevronDown className="dls-h-4 dls-w-4" />}
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="dls-pt-0 dls-flex dls-flex-col dls-flex-1 dls-gap-3">
        {hasFlagged ? (
          <>
            <div className="dls-grid dls-grid-cols-2 dls-gap-3 dls-text-sm">
              <div
                className={`dls-rounded-md dls-p-2 dls-border dls-flex dls-items-baseline dls-gap-1.5 ${hasCritical ? 'dls-bg-status-offline-quarternary dls-border-status-offline-tertiary' : 'dls-bg-white/80 dls-border-status-warning-tertiary'}`}
              >
                <span
                  className={`dls-text-lg dls-font-semibold ${resourceAlerts.criticalCount > 0 ? 'dls-text-status-offline' : 'dls-text-muted-foreground'}`}
                >
                  {resourceAlerts.criticalCount}
                </span>
                <span className="dls-text-xs dls-text-muted-foreground">Critical (&gt;90%)</span>
              </div>
              <div className="dls-rounded-md dls-bg-status-warning-quarternary dls-p-2 dls-border dls-border-status-warning-tertiary dls-flex dls-items-baseline dls-gap-1.5">
                <span className="dls-text-lg dls-font-semibold dls-text-status-warning">
                  {resourceAlerts.cautionCount}
                </span>
                <span className="dls-text-xs dls-text-muted-foreground">Caution (70–90%)</span>
              </div>
            </div>

            {expanded && (
              <>
                <div className="dls-max-h-48 dls-overflow-y-auto">
                  <table className="dls-w-full dls-text-xs">
                    <thead>
                      <tr className="dls-text-muted-foreground dls-border-b">
                        <th className="dls-text-left dls-py-1 dls-font-medium">Node</th>
                        <th className="dls-text-left dls-py-1 dls-font-medium">S/N</th>
                        <th className="dls-text-left dls-py-1 dls-font-medium">Vendor</th>
                        <th className="dls-text-left dls-py-1 dls-font-medium">Model</th>
                        <th className="dls-text-center dls-py-1 dls-pr-2 dls-font-medium dls-w-28">Resources</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resourceAlerts.items.map((item) => (
                        <tr key={item.nodeId} className="dls-border-b last:dls-border-0">
                          <td className="dls-py-1.5">
                            <div className="dls-font-medium">{item.nodeName}</div>
                            <div className="dls-text-muted-foreground">{item.portfolio ?? 'Unassigned'}</div>
                          </td>
                          <td className="dls-py-1.5 dls-font-mono dls-text-muted-foreground">
                            {item.hardwareSerialNumber ?? '-'}
                          </td>
                          <td className="dls-py-1.5 dls-text-muted-foreground">
                            {item.hardwareVendor ?? '-'}
                          </td>
                          <td className="dls-py-1.5 dls-text-muted-foreground">
                            {item.hardwareModel ?? '-'}
                          </td>
                          <td className="dls-py-1.5 dls-pr-2 dls-space-y-0.5">
                            <UsageBar label="CPU" value={item.cpuUsage} />
                            <UsageBar label="RAM" value={item.memoryUsage} />
                            <UsageBar label="Disk" value={item.diskUsage} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div
                  className={`dls-flex dls-items-center dls-gap-2 dls-rounded-md dls-p-2 dls-border dls-text-xs dls-mt-auto dls-flex-shrink-0 ${hasCritical ? 'dls-bg-status-offline-quarternary dls-border-status-offline-tertiary dls-text-status-offline' : 'dls-bg-status-warning-quarternary dls-border-status-warning-tertiary dls-text-status-warning'}`}
                >
                  <AlertTriangle className="dls-h-3.5 dls-w-3.5 dls-flex-shrink-0" />
                  {hasCritical ? (
                    <span>
                      {resourceAlerts.criticalCount} node
                      {resourceAlerts.criticalCount !== 1 ? 's have' : ' has'} resources above 90%.
                      Investigate for runaway processes or plan capacity upgrades.
                    </span>
                  ) : (
                    <span>
                      Monitor elevated resource usage. Consider rebalancing workloads if usage remains
                      above 70%.
                    </span>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="dls-flex dls-flex-col dls-gap-3">
            <div className="dls-flex dls-items-center dls-gap-2 dls-text-sm dls-text-status-online">
              <CheckCircle2 className="dls-h-4 dls-w-4 dls-flex-shrink-0" />
              <span>No nodes above resource thresholds</span>
            </div>
            <div className="dls-grid dls-grid-cols-2 dls-gap-3 dls-text-sm">
              <div className="dls-rounded-md dls-bg-white/80 dls-p-2 dls-border dls-border-status-online-tertiary dls-flex dls-items-baseline dls-gap-1.5">
                <span className="dls-text-lg dls-font-semibold dls-text-status-online">
                  {totalReporting}
                </span>
                <span className="dls-text-xs dls-text-muted-foreground">
                  of {totalAlive} nodes reporting
                </span>
              </div>
              <div className="dls-rounded-md dls-bg-white/80 dls-p-2 dls-border dls-border-status-online-tertiary dls-flex dls-items-baseline dls-gap-1.5">
                <span className="dls-text-lg dls-font-semibold dls-text-status-online">
                  {coveragePct}%
                </span>
                <span className="dls-text-xs dls-text-muted-foreground">stats coverage</span>
              </div>
            </div>
            {coveragePct < 50 && totalAlive > 0 && (
              <div className="dls-flex dls-items-center dls-gap-2 dls-rounded-md dls-p-2 dls-border dls-bg-status-warning-quarternary dls-border-status-warning-tertiary dls-text-xs dls-text-status-warning">
                <AlertTriangle className="dls-h-3.5 dls-w-3.5 dls-flex-shrink-0" />
                <span>
                  Low stats coverage. {totalAlive - totalReporting} node{totalAlive - totalReporting !== 1 ? 's are' : ' is'} not
                  reporting resource metrics. Check node monitoring configuration.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Coverage bar — always show when there are flagged items too */}
        {hasFlagged && totalAlive > 0 && (
          <div className="dls-text-xs dls-text-muted-foreground">
            Stats coverage: {totalReporting} of {totalAlive} nodes ({coveragePct}%)
          </div>
        )}
      </CardContent>
    </Card>
  );
}
